import React from 'react';
import { DeadlineKind, EmailKey, StepCard, StepOptions, Worklist } from '../../../api/cafAutomation';
import { CatalogueReviews, emailOptionLink } from '../../../api/emailCatalogue';
import CatalogueSelect from '../../email-catalogue/CatalogueSelect';
import { Icon } from '../Icon';
import { KebabMenu, MenuItem } from '../KebabMenu';
import { ForwardStepFields } from './ForwardStepFields';
import {
  CardKind, DEADLINE_NAMES, EMAIL_NAMES, MARK_AS_MANUAL, MENU_KINDS, STEP_TITLES, StepProblem, emailsClient, isChecked, isEnding,
  listsOffered, moveCard, newCard, sendsEmail,
  isConfiguredForward, stepProblems, stepTitle,
} from './steps';

interface Props {
  idPrefix: string;
  cards: StepCard[];
  onChange: (cards: StepCard[]) => void;
  options: StepOptions | null;
  reviewed: Record<string, string>;
  reviewedCatalogue?: CatalogueReviews;
  onPreview: (index: number) => void;
  disabled?: boolean;
  /** The loaded workflow's emails differ from the current settings (WorkflowView.settings_changed). */
  settingsChanged?: boolean;
  /** A step refusal from the server: index from stepIndexFromPath(error.path), or null when it is not about one card. */
  refusal?: { index: number | null; message: string } | null;
}

const MENU_ICONS: Record<CardKind, MenuItem['icon']> = {
  attach: 'clip', note: 'pencil', email: 'mail', catalogue_email: 'mail', forward: 'mail', wait: 'clock', cancel_claim: 'x', set_deadline: 'clock',
  done: 'check', archive: 'list', manual: 'question',
};

export const StepEditor: React.FC<Props> = ({ idPrefix, cards, onChange, options, reviewed, onPreview, disabled = false,
  settingsChanged = false, refusal = null, reviewedCatalogue = {},
}) => {
  // Final review C8: list wording only when the server offers lists.
  const lists = listsOffered(options);
  const problems: StepProblem[] = [...stepProblems(cards, lists ?? true), ...(refusal ? [{ ...refusal, index: refusal.index !== null && refusal.index >= 0 && refusal.index < cards.length ? refusal.index : null }] : [])];
  const listProblems = problems.filter((problem) => problem.index === null);
  const cardProblems = (index: number): StepProblem[] => problems.filter((problem) => problem.index === index);
  const replace = (index: number, card: StepCard) => onChange(cards.map((item, position) => (position === index ? card : item)));

  const add = (kind: CardKind) => {
    const card = newCard(kind, options);
    const last = cards[cards.length - 1];
    if (last && isEnding(last) && !isEnding(card)) onChange([...cards.slice(0, -1), card, last]);
    else onChange([...cards, card]);
  };

  const addItems: MenuItem[] = MENU_KINDS.filter((kind) => kind !== 'set_deadline' || Boolean(options?.deadline_kinds))
    .map((kind): MenuItem => ({
      key: kind, label: kind === 'manual' && !lists ? MARK_AS_MANUAL : STEP_TITLES[kind], icon: MENU_ICONS[kind], onSelect: () => add(kind),
    }));
  addItems.splice(4, 0, {
    key: 'reminder', label: 'Send a reminder', icon: 'clock', disabled: true,
    hint: 'The multi-rep email sends its own reminder when reminders are on.', onSelect: () => undefined,
  });

  const body = (card: StepCard, index: number) => {
    const n = index + 1;
    if (card.kind === 'attach') return (
      <>
        <p>Uses the claim the AI matched.</p>
        <p className="cur-hint">Links the received email to the claim record. This does not add attachments to an outgoing email.</p>
      </>
    );
    if (card.kind === 'note') {
      return (
        <textarea className="cur-input" rows={2} maxLength={4000} aria-label={`Note for step ${n}`} value={card.text}
          disabled={disabled} placeholder="What to write on the claim"
          onChange={(event) => replace(index, { kind: 'note', text: event.target.value })} />
      );
    }
    if (card.kind === 'email' || card.kind === 'catalogue_email') {
      const option = card.kind === 'email' ? options?.emails.find((item) => item.key === card.email) : undefined;
      const family = card.kind === 'email' ? { loa: 'loa_portal', multi_rep: 'multi_rep', cancel_confirmation: 'cancel_confirmation' }[card.email] : 'generic';
      const selected = card.kind === 'catalogue_email' ? 'catalogue:' + card.catalogue_ref.option_id : card.email;
      return (
        <>
          <div className="cur-inline">
            Email
            <select className="cur-input" aria-label={`Email for step ${n}`} value={selected} disabled={disabled}
              onChange={(event) => {
                const chosen = options?.catalogue_options?.find(item => 'catalogue:' + item.ref.option_id === event.target.value);
                if (chosen?.status === 'ready') replace(index, { kind: 'catalogue_email', catalogue_ref: chosen.ref });
                else if (event.target.value === 'catalogue:') replace(index, newCard('catalogue_email', options));
                else if (options?.emails.some(item => item.key === event.target.value)) replace(index, { kind: 'email', email: event.target.value as EmailKey });
              }}>
              {(options?.emails || []).map((item) => (
                <option key={item.key} value={item.key} disabled={!item.available}>
                  {`${EMAIL_NAMES[item.key]}${item.available ? '' : ' (not set up)'}`}
                </option>
              ))}
              <option value="catalogue:">Choose another catalogue email…</option>
              {card.kind === 'catalogue_email' && !options?.catalogue_options?.some(item => item.ref.option_id === card.catalogue_ref.option_id)
                && card.catalogue_ref.option_id && <option value={selected}>{card.catalogue_ref.option_id} · saved settings</option>}
              {options?.catalogue_options?.filter(item => item.config.family === 'generic').map(item => <option
                key={item.ref.option_id} value={'catalogue:' + item.ref.option_id} disabled={item.status !== 'ready'}>{item.config.name} · revision {item.ref.revision}{item.status !== 'ready' ? ' (not ready)' : ''}</option>)}
            </select>
            {card.kind === 'email' ? 'to the client' : 'using its configured recipient'}
          </div>
          {option?.reminder && <p className="cur-hint">It sends its own reminder after 7 days if the client doesn&apos;t reply.</p>}
          {options?.catalogue_options && <CatalogueSelect label={`Catalogue settings for step ${n}`} family={family}
            value={card.catalogue_ref} options={options.catalogue_options} nextCursor={options.catalogue_next_cursor} disabled={disabled}
            onChange={catalogue_ref => replace(index, { ...card, catalogue_ref })} />}
          {card.catalogue_ref?.option_id && <a href={emailOptionLink(card.catalogue_ref.option_id)} target="_blank" rel="noopener noreferrer">Edit email settings</a>}
          {card.kind === 'email' && card.email === 'multi_rep' && option?.reminder && options?.catalogue_options &&
            <CatalogueSelect label={`Reminder settings for step ${n}`} family="multi_rep_reminder" value={card.reminder_catalogue_ref}
              options={options.catalogue_options} nextCursor={options.catalogue_next_cursor} disabled={disabled}
              onChange={reminder_catalogue_ref => replace(index, { ...card, reminder_catalogue_ref })} />}
          {card.kind === 'email' && card.reminder_catalogue_ref?.option_id && <a href={emailOptionLink(card.reminder_catalogue_ref.option_id)} target="_blank" rel="noopener noreferrer">Edit reminder settings</a>}
        </>
      );
    }
    if (isConfiguredForward(card)) return <>{card.forwarding_scope === 'closed_claim_default' && <p>Saved scope: cancelled claims only. Selecting email settings preserves this scope.</p>}
      <ForwardStepFields value={card.covering_email} scope={card.forwarding_scope} index={index} options={options} disabled={disabled}
        onChange={(covering_email) => replace(index, { ...card, covering_email })} /></>;
    if (card.kind === 'forward') return (
      <>
        <p>Sends the configured covering email to the client on the matched claim.</p>
        <p><strong>Attachments:</strong> the received lender email as a PDF, plus all its original attachments.</p>
        <p className="cur-hint">This step currently works only for cancelled claims.</p>
      </>
    );
    if (card.kind === 'wait') {
      return (
        <>
          <div className="cur-inline">
            Wait
            <select className="cur-input" aria-label={`Days to wait for step ${n}`} value={card.days} disabled={disabled}
              onChange={(event) => replace(index, { kind: 'wait', days: Number(event.target.value) })}>
              {(options?.wait_days || [card.days]).map((days) => <option key={days} value={days}>{days}</option>)}
            </select>
            days for the client
          </div>
          <p className="cur-hint">If they reply, the workflow stops for good and the task is marked as manual.</p>
        </>
      );
    }
    if (card.kind === 'cancel_claim') {
      return (
        <>
          <div className="cur-inline">
            Reason
            <select className="cur-input" aria-label={`Reason for step ${n}`} value={card.reason} disabled={disabled}
              onChange={(event) => replace(index, { ...card, reason: event.target.value, ...(card.catalogue_emails ? { catalogue_emails: {} } : {}) })}>
              {(options?.cancel_reasons || [card.reason]).map((reason) => <option key={reason} value={reason}>{reason}</option>)}
            </select>
          </div>
          {options?.catalogue_options && (!card.catalogue_emails ? <button type="button" disabled={disabled}
            onClick={() => replace(index, { ...card, catalogue_emails: {} })}>Choose catalogue cancellation emails</button>
            : (options.cancellation_roles?.[card.reason] || []).map(role => <React.Fragment key={role.role}><CatalogueSelect
              label={role.purpose} family={role.role} value={card.catalogue_emails?.[role.role]} options={options.catalogue_options}
              nextCursor={options.catalogue_next_cursor} disabled={disabled}
              onChange={ref => replace(index, { ...card, catalogue_emails: { ...card.catalogue_emails, [role.role]: ref } })} />
              {card.catalogue_emails?.[role.role]?.option_id && <a href={emailOptionLink(card.catalogue_emails[role.role].option_id)} target="_blank" rel="noopener noreferrer">Edit {role.purpose} settings</a>}</React.Fragment>))}
          <div className="cur-inline" role="radiogroup" aria-label={`Fee for step ${n}`}>
            <label className="cur-check"><input type="radio" name={`${idPrefix}-fee-${index}`} checked={card.fee === 'usual'} disabled={disabled}
              onChange={() => replace(index, { ...card, fee: 'usual' })} /><span>Charge the usual fee</span></label>
            <label className="cur-check"><input type="radio" name={`${idPrefix}-fee-${index}`} checked={card.fee === 'none'} disabled={disabled}
              onChange={() => replace(index, { ...card, fee: 'none' })} /><span>No fee</span></label>
          </div>
        </>
      );
    }
    if (card.kind === 'set_deadline') return (
      <>
        <div className="cur-inline">
          Deadline
          <select className="cur-input" aria-label={`Deadline for step ${n}`} value={card.deadline} disabled={disabled}
            onChange={(event) => replace(index, { kind: 'set_deadline', deadline: event.target.value as DeadlineKind })}>
            {(options?.deadline_kinds || [{ key: card.deadline, label: DEADLINE_NAMES[card.deadline] }]).map((item) =>
              <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
        </div>
        <p className="cur-hint">Uses the date in the letter. If that can&apos;t be read, it uses the date we received the letter and marks the deadline as estimated.</p>
        <p className="cur-hint">Staff get a reminder 7, 3 and 1 days before.</p>
      </>
    );
    if (card.kind === 'done') return <p>The task completes on its own.</p>;
    if (card.kind === 'archive') return <p>The email is archived and the task completes on its own.</p>;
    if (card.kind !== 'manual') return null;
    return (
      <>
        {options?.worklists && (
          <div className="cur-inline">
            List
            <select className="cur-input" aria-label={`List for step ${n}`} value={card.worklist ?? 'not_sure'} disabled={disabled}
              onChange={(event) => replace(index, { ...card, worklist: event.target.value as Worklist })}>
              {options.worklists.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </div>
        )}
        <input className="cur-input" aria-label={`Why a person is needed (optional), step ${n}`} value={card.note} disabled={disabled}
          placeholder="Why a person is needed (optional)" maxLength={1000}
          onChange={(event) => replace(index, { ...card, note: event.target.value })} />
        <p className="cur-hint">
          {lists ? 'It stays open in the Lender lane, on this list, for a person to finish.' : 'It becomes a normal manual task in the Lender lane.'}
        </p>
      </>
    );
  };

  return (
    <div className="cur-stack">
      <div className="cur-callout cur-callout--info">
        <Icon name="info" size="sm" />
        <span className="cur-grow">
          Every workflow ends by marking the task done, archiving it, or {lists ? 'putting it on a list' : 'marking it as manual'}. If the client replies, it stops and the task is marked as manual.
        </span>
      </div>
      {settingsChanged && (
        <div className="cur-callout cur-callout--info">
          <Icon name="info" size="sm" />
          <span className="cur-grow">Its emails differ from the current settings. Review the selected settings before saving.</span>
        </div>
      )}
      {listProblems.map((problem) => <p key={problem.message} className="cur-problem" role="alert">{problem.message}</p>)}
      <ol className="cur-steps" aria-label="Steps">
        {cards.map((card, index) => {
          const own = cardProblems(index);
          const sends = sendsEmail(card);
          const checked = isChecked(card, options, reviewed, index, reviewedCatalogue);
          const menu: MenuItem[] = [
            { key: 'up', label: 'Move up', icon: 'arrow-up', disabled: index === 0, onSelect: () => onChange(moveCard(cards, index, -1)) },
            { key: 'down', label: 'Move down', icon: 'arrow-down', disabled: index === cards.length - 1, onSelect: () => onChange(moveCard(cards, index, 1)) },
            { key: 'remove', label: 'Remove step', icon: 'trash', danger: true, separatorBefore: true, onSelect: () => onChange(cards.filter((_, position) => position !== index)) },
          ];
          return (
            // Cards have no identity of their own; their position is their identity here.
            <li key={index} className={`cur-step${own.length ? ' has-problem' : ''}`}>
              <span className="cur-step-n" aria-hidden="true">{index + 1}</span>
              <div>
                <div className="cur-step-title">
                  {stepTitle(card, lists)}
                  {emailsClient(card) && <span className="cur-pill cur-pill--blue"><Icon name="mail" size="xs" />{card.kind === 'catalogue_email' ? 'Sends an email' : 'Emails the client'}</span>}
                  {isEnding(card) && <span className="cur-pill cur-pill--teal">Ending</span>}
                  {card.kind === 'cancel_claim' && <span className="cur-pill cur-pill--blue"><Icon name="mail" size="xs" />Sends emails</span>}
                  {sends && (checked
                    ? <span className="cur-pill cur-pill--teal">Checked</span>
                    : <span className="cur-pill cur-pill--amber">Not checked yet</span>)}
                </div>
                <div className="cur-step-body">
                  {body(card, index)}
                  {sends && (
                    <div>
                      <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" disabled={disabled} onClick={() => onPreview(index)}>
                        <Icon name="eye" size="sm" />Preview
                      </button>
                    </div>
                  )}
                  {own.map((problem) => <p key={problem.message} className="cur-problem">{problem.message}</p>)}
                </div>
              </div>
              {!disabled && <KebabMenu label={`Actions for step ${index + 1}`} items={menu} />}
            </li>
          );
        })}
      </ol>
      {!disabled && <div><KebabMenu label="Add a step" triggerText="Add a step" items={addItems} /></div>}
    </div>
  );
};
